import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { FileText, ChevronDown, ChevronUp, Phone, Clock, Bot, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import ReactMarkdown from 'react-markdown';

interface CallSummary {
  id: string;
  caller_number: string;
  ai_summary: string;
  transcription: string | null;
  duration_seconds: number | null;
  ivr_selection: string | null;
  direction: string;
  created_at: string;
  customer_name: string | null;
}

interface CallSummaryRow {
  id: string;
  caller_number: string;
  ai_summary: string;
  transcription: string | null;
  duration_seconds: number | null;
  ivr_selection: string | null;
  direction: string;
  created_at: string;
  customers: {
    name: string | null;
  } | null;
}

export function CallSummariesTab() {
  const { user } = useAuth();
  const companyId = user?.company_id;
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleCreateCase = useCallback((summary: CallSummary) => {
    const transcript = summary.ai_summary;
    sessionStorage.setItem('wizard_prefill_transcript', transcript);
    sessionStorage.setItem('wizard_prefill_source', 'call_summary');
    navigate('/office/cases/new');
  }, [navigate]);

  const { data: summaries = [], isLoading, error } = useQuery({
    queryKey: ['call-summaries', companyId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('switchboard_calls')
        .select('id, caller_number, ai_summary, transcription, duration_seconds, ivr_selection, direction, created_at, customers:customer_id(name)')
        .eq('company_id', companyId)
        .not('ai_summary', 'is', null)
        .order('created_at', { ascending: false })
        .limit(30);

      if (error) throw error;

      return ((data ?? []) as CallSummaryRow[]).map(({ customers, ...summary }) => ({
        ...summary,
        customer_name: customers?.name ?? null,
      })) as CallSummary[];
    },
    enabled: !!companyId,
    refetchInterval: 30_000,
  });

  const fmtDuration = (secs: number | null) => {
    if (!secs) return '—';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="px-3 py-6 text-center">
        <p className="text-[11px] text-muted-foreground animate-pulse">Henter resuméer...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-3 py-6 text-center space-y-1">
        <Bot className="w-5 h-5 text-destructive mx-auto" />
        <p className="text-[11px] text-muted-foreground">Kunne ikke hente opkaldsresuméer</p>
      </div>
    );
  }

  if (summaries.length === 0) {
    return (
      <div className="px-3 py-6 text-center space-y-1">
        <Bot className="w-5 h-5 text-muted-foreground mx-auto" />
        <p className="text-[11px] text-muted-foreground">Ingen opkaldsresuméer endnu</p>
      </div>
    );
  }

  return (
    <ScrollArea className="max-h-[70vh]">
      <div className="px-2 py-1 space-y-1">
        {summaries.map((s) => {
          const isOpen = expandedId === s.id;
          return (
            <div key={s.id} className="rounded-md border border-border bg-card overflow-hidden">
              <button
                className="w-full text-left px-2.5 py-2 flex items-center gap-2 hover:bg-accent/50 transition-colors"
                onClick={() => setExpandedId(isOpen ? null : s.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="text-xs font-medium text-foreground truncate">
                      {s.customer_name || s.caller_number}
                    </span>
                    {s.ivr_selection && (
                      <Badge variant="outline" className="text-[8px] h-4 px-1 shrink-0">
                        {s.ivr_selection}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-muted-foreground">
                      {format(new Date(s.created_at), 'd. MMM HH:mm', { locale: da })}
                    </span>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                      <Clock className="w-2.5 h-2.5" />
                      {fmtDuration(s.duration_seconds)}
                    </span>
                  </div>
                </div>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); handleCreateCase(s); }}
                  className="shrink-0 p-1 rounded hover:bg-primary/10 text-primary transition-colors"
                  title="Opret sag"
                >
                  <Plus className="w-3.5 h-3.5" />
                </span>
                {isOpen ? (
                  <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                )}
              </button>

              {isOpen && (
                <div className="px-2.5 pb-2.5 border-t border-border pt-2 space-y-2">
                  <div className="max-h-[300px] overflow-y-auto">
                    <div className="prose prose-xs dark:prose-invert max-w-none text-[11px] leading-relaxed [&_h2]:text-xs [&_h2]:font-semibold [&_h2]:mt-1 [&_h2]:mb-0.5 [&_h3]:text-[11px] [&_h3]:font-semibold [&_h3]:mt-1 [&_h3]:mb-0.5 [&_ul]:my-0.5 [&_li]:my-0 [&_p]:my-0.5 [&_hr]:my-1">
                      <ReactMarkdown>{s.ai_summary}</ReactMarkdown>
                    </div>
                  </div>
                    {s.transcription && (
                      <details className="group">
                        <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground transition-colors flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          Vis transskription
                        </summary>
                        <div className="mt-1 p-2 bg-muted/50 rounded text-[10px] text-muted-foreground whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                          {s.transcription}
                        </div>
                      </details>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCreateCase(s); }}
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-primary text-primary-foreground text-[11px] font-medium hover:bg-primary/90 transition-colors mt-1"
                    >
                      <Plus className="w-3 h-3" />
                      Opret sag fra opkald
                    </button>
                  </div>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
